import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button, Label, Input } from "@tellescope/design-system";
import { ProductListSection } from "./ProductListSection";
import { useShopBlockEditorLogic } from "./useShopBlockEditorLogic";
import type { SelectedShopEntry } from "./types";
import type { ShopProduct } from "../enduser/types";

export interface ShopBlockEditorProps {
  title: string | undefined;
  subtitle: string | undefined;
  emptyMessage: string | undefined;
  confirmationMessage: string | undefined;
  products: ShopProduct[];
  stripeKey: string | undefined;
  onSave: (props: {
    title: string | undefined;
    subtitle: string | undefined;
    emptyMessage: string | undefined;
    confirmationMessage: string | undefined;
    products: ShopProduct[];
    stripeKey: string | undefined;
  }) => void;
}

const toSelectedShopEntry = (product: ShopProduct): SelectedShopEntry => ({
  stripeProductId: product.stripeProductId,
  title: product.title,
  description: product.description,
  image: product.image,
  formId: product.formId,
  variants: product.variants,
  badge: product.badge,
  recommended: product.recommended,
  category: product.category,
  visibility: product.visibility,
});

export const ShopBlockEditor = ({
  title,
  subtitle,
  emptyMessage,
  confirmationMessage,
  products,
  stripeKey,
  onSave,
}: ShopBlockEditorProps) => {
  const { t } = useTranslation("shop");
  const [titleInput, setTitleInput] = useState(title ?? "");
  const [subtitleInput, setSubtitleInput] = useState(subtitle ?? "");
  const [emptyMessageInput, setEmptyMessageInput] = useState(emptyMessage ?? "");
  const [confirmationMessageInput, setConfirmationMessageInput] = useState(confirmationMessage ?? "");

  const logic = useShopBlockEditorLogic({
    initialSelection: products.map(toSelectedShopEntry),
    initialStripeKey: stripeKey ?? null,
  });

  const handleSave = () => {
    const snapshot = logic.buildSnapshot();
    if (!snapshot) return;
    onSave({
      title: titleInput || undefined,
      subtitle: subtitleInput || undefined,
      emptyMessage: emptyMessageInput || undefined,
      confirmationMessage: confirmationMessageInput || undefined,
      products: snapshot,
      stripeKey: logic.stripeKey ?? undefined,
    });
    // This only stages the selection into the in-memory schema draft (updateProps in
    // ShopBlockEditorAdapter) — there's no request and no error path, unlike every other "Save"
    // in the admin dashboard, which persists to the server. Without this toast the click has no
    // visible effect at all, since the button's own appearance doesn't change either way.
    toast.success(t("Product selection staged — save the portal configuration to publish it"));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("Heading (optional)")}</Label>
        <Input value={titleInput} onChange={(e) => setTitleInput(e.target.value)} placeholder={t("All Products")} />
      </div>

      <div className="space-y-2">
        <Label>{t("Subtitle (optional)")}</Label>
        <Input
          value={subtitleInput}
          onChange={(e) => setSubtitleInput(e.target.value)}
          placeholder={t("Browse our curated selection of medications and supplements")}
        />
      </div>

      <ProductListSection logic={logic} />

      <div className="space-y-2 pt-2 border-t">
        {/* Shared across every block editor's empty-state field — stays in the common namespace, not shop-specific. */}
        <Label>{t("Empty Message (optional)", { ns: "common" })}</Label>
        <Input
          value={emptyMessageInput}
          onChange={(e) => setEmptyMessageInput(e.target.value)}
          placeholder={t("No products available")}
        />
      </div>

      <div className="space-y-2">
        <Label>{t("Order Confirmation Message (optional)")}</Label>
        <Input
          value={confirmationMessageInput}
          onChange={(e) => setConfirmationMessageInput(e.target.value)}
          placeholder={t("Your order has been placed.")}
        />
      </div>

      {/* "Save" is shared across every block editor's save button — stays in the common namespace, not shop-specific. */}
      <Button onClick={handleSave} disabled={!logic.canSave} loading={logic.resolutionLoading}>
        {t("Save", { ns: "common" })}
      </Button>
      <p className="text-xs text-muted-foreground">
        {t(
          "This updates the block's product selection in your draft. It won't appear for endusers until you also save the portal configuration."
        )}
      </p>
      {logic.unresolvedSelections.length > 0 && (
        <p className="text-xs text-destructive">{t("Resolve the unconfigured product(s) above before saving.")}</p>
      )}
    </div>
  );
};
